<script lang="ts">
  import { onMount } from 'svelte'
  import { api } from '$lib/api/client'
  import type { paths } from '$lib/api/generated'
  import Table from '../../components/Table.svelte'
  import SearchInput from '../../components/SearchInput.svelte'
  import Modal from '../../components/Modal.svelte'
  import FormField from '../../components/FormField.svelte'
  import { showApiError, showError } from '$lib/util/errors'
  import { debounce } from '$lib/util/debounce'

  type UnitWithPM =
    paths['/units']['get']['responses'][200]['content']['application/json']['items'][number]
  type PropertyManager =
    paths['/property-managers']['get']['responses'][200]['content']['application/json']['items'][number]
  type Property =
    paths['/property-managers/{id}/properties']['get']['responses'][200]['content']['application/json']['items'][number]

  let items = $state<UnitWithPM[]>([])
  let nextCursor = $state<number | undefined>(undefined)
  let search = $state('')
  let pmFilter = $state<number | ''>('')
  let loading = $state(false)
  let showModal = $state(false)
  let editing = $state<UnitWithPM | null>(null)

  // Form fields
  let propertyManagerId = $state<number | null>(null)
  let propertyId = $state<number | null>(null)
  let streetAddress = $state('')
  let unitNumber = $state('')
  let city = $state('')
  let addressState = $state('')
  let zipCode = $state('')
  let bedrooms = $state('')
  let bathrooms = $state('')
  let squareFeet = $state('')

  let pmOptions = $state<PropertyManager[]>([])
  let propertyOptions = $state<Property[]>([])

  async function loadProperties(pmId: number): Promise<void> {
    const { data, error } = await api.GET('/property-managers/{id}/properties', {
      params: { path: { id: pmId } },
    })
    if (error) {
      showApiError(error)
      return
    }
    propertyOptions = data?.items ?? []
  }

  interface FlatUnit {
    id: number
    streetAddress: string
    city: string
    state: string
    pmName: string
  }

  const rows: () => FlatUnit[] = () =>
    items.map((u) => ({
      id: u.id,
      streetAddress: u.streetAddress,
      city: u.city,
      state: u.state,
      pmName: u.propertyManager.name,
    }))

  async function loadPMOptions(): Promise<void> {
    const { data, error } = await api.GET('/property-managers', {
      params: { query: { limit: 50 } },
    })
    if (error) {
      showApiError(error)
      return
    }
    pmOptions = data?.items ?? []
  }

  async function load(cursor?: number): Promise<void> {
    loading = true
    const { data, error } = await api.GET('/units', {
      params: {
        query: {
          limit: 50,
          ...(cursor !== undefined ? { cursor } : {}),
          ...(search ? { search } : {}),
          ...(pmFilter !== '' ? { propertyManagerId: Number(pmFilter) } : {}),
        },
      },
    })
    if (error) {
      showApiError(error)
      loading = false
      return
    }
    items = cursor !== undefined ? [...items, ...(data?.items ?? [])] : (data?.items ?? [])
    nextCursor = data?.nextCursor
    loading = false
  }

  function openCreate(): void {
    editing = null
    propertyManagerId = null
    propertyId = null
    propertyOptions = []
    streetAddress = ''
    unitNumber = ''
    city = ''
    addressState = ''
    zipCode = ''
    bedrooms = ''
    bathrooms = ''
    squareFeet = ''
    void loadPMOptions()
    showModal = true
  }

  function openEdit(row: FlatUnit): void {
    const found = items.find((u) => u.id === row.id)
    if (!found) return
    editing = found
    propertyManagerId = found.propertyManager.id
    propertyId = found.propertyId ?? null
    propertyOptions = []
    void loadProperties(found.propertyManager.id)
    streetAddress = found.streetAddress
    unitNumber = found.unitNumber ?? ''
    city = found.city
    addressState = found.state
    zipCode = found.zipCode
    bedrooms = found.bedrooms !== undefined ? String(found.bedrooms) : ''
    bathrooms = found.bathrooms !== undefined ? String(found.bathrooms) : ''
    squareFeet = found.squareFeet !== undefined ? String(found.squareFeet) : ''
    void loadPMOptions()
    showModal = true
  }

  async function handleSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault()
    if (propertyManagerId === null) {
      showError({ detail: 'Property manager is required.' })
      return
    }
    const body = {
      propertyManagerId,
      streetAddress,
      ...(unitNumber ? { unitNumber } : {}),
      city,
      state: addressState,
      zipCode,
      ...(bedrooms ? { bedrooms: Number(bedrooms) } : {}),
      ...(bathrooms ? { bathrooms: Number(bathrooms) } : {}),
      ...(squareFeet ? { squareFeet: Number(squareFeet) } : {}),
    }
    if (editing) {
      const { data, error } = await api.PUT('/units/{id}', {
        params: { path: { id: editing.id } },
        // null clears the assignment; a number assigns it.
        body: { ...body, propertyId },
      })
      if (error || !data) {
        showApiError(error)
        return
      }
      items = items.map((u) => (u.id === editing?.id ? data : u))
    } else {
      const { data, error } = await api.POST('/units', {
        body: { ...body, ...(propertyId !== null ? { propertyId } : {}) },
      })
      if (error || !data) {
        showApiError(error)
        return
      }
      items = [data, ...items]
    }
    showModal = false
  }

  async function handleDelete(id: number): Promise<void> {
    if (!confirm('Delete this unit?')) return
    const { error } = await api.DELETE('/units/{id}', { params: { path: { id } } })
    if (error) {
      showApiError(error)
      return
    }
    items = items.filter((u) => u.id !== id)
  }

  const searchDebounced = debounce(() => void load(), 300)
  $effect(() => {
    void search
    searchDebounced.call()
    return () => searchDebounced.cancel()
  })

  $effect(() => {
    void pmFilter
    void load()
  })

  onMount(() => {
    void loadPMOptions()
  })
</script>

<div class="section-header">
  <h1>Units</h1>
  <div class="section-actions">
    <label>
      Property Manager:
      <select bind:value={pmFilter}>
        <option value="">All</option>
        {#each pmOptions as pm (pm.id)}
          <option value={pm.id}>{pm.name}</option>
        {/each}
      </select>
    </label>
    <SearchInput bind:value={search} placeholder="Search street or city" />
    <button type="button" class="primary" onclick={openCreate}>+ Add unit</button>
  </div>
</div>

<Table
  items={rows()}
  {loading}
  columns={[
    { key: 'streetAddress', label: 'Street' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'pmName', label: 'Property Manager' },
  ]}
  onEdit={openEdit}
  onDelete={handleDelete}
/>

{#if nextCursor !== undefined}
  <button type="button" class="load-more" onclick={() => void load(nextCursor)} disabled={loading}>
    {loading ? 'Loading…' : 'Load more'}
  </button>
{/if}

{#if showModal}
  <Modal title={editing ? 'Edit unit' : 'Add unit'} onclose={() => (showModal = false)}>
    <form onsubmit={handleSubmit}>
      <div class="form-field">
        <label for="pm">Property Manager *</label>
        <select
          id="pm"
          bind:value={propertyManagerId}
          onchange={() => {
            propertyId = null
            propertyOptions = []
            if (propertyManagerId !== null) void loadProperties(propertyManagerId)
          }}
          required
        >
          <option value={null}>- Select -</option>
          {#each pmOptions as pm (pm.id)}
            <option value={pm.id}>{pm.name}</option>
          {/each}
        </select>
      </div>
      <div class="form-field">
        <label for="property">Property</label>
        <select id="property" bind:value={propertyId} disabled={propertyManagerId === null}>
          <option value={null}>- Ungrouped -</option>
          {#each propertyOptions as p (p.id)}
            <option value={p.id}>{p.name}</option>
          {/each}
        </select>
      </div>
      <FormField label="Street address" name="streetAddress" bind:value={streetAddress} required />
      <FormField label="Unit #" name="unitNumber" bind:value={unitNumber} />
      <FormField label="City" name="city" bind:value={city} required />
      <FormField label="State" name="state" bind:value={addressState} required />
      <FormField label="ZIP code" name="zipCode" bind:value={zipCode} required />
      <FormField label="Bedrooms" name="bedrooms" type="number" bind:value={bedrooms} />
      <FormField label="Bathrooms" name="bathrooms" type="number" bind:value={bathrooms} />
      <FormField label="Square feet" name="squareFeet" type="number" bind:value={squareFeet} />
      <button type="submit" class="primary">Save</button>
    </form>
  </Modal>
{/if}

<style>
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 8px;
  }
  h1 {
    margin: 0;
    font-size: 1.75rem;
  }
  .section-actions {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
  }
  .section-actions label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
  }
  .section-actions select {
    padding: 4px 8px;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 0.875rem;
  }
  button.primary {
    background: var(--brand);
    color: white;
    padding: 6px 14px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
  }
  button.primary:hover {
    background: var(--brand-hover);
  }
  button.load-more {
    padding: 8px 16px;
    background: var(--surface-2);
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    cursor: pointer;
  }
  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
  }
  .form-field label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--ink);
  }
  .form-field select {
    padding: 6px 10px;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 0.875rem;
  }
</style>
